import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { isWalletUnlocked as checkWalletUnlocked, setWalletSession, clearWalletSession, getWalletSession } from '../auth/walletSession';
import { lockWallet as backendLockWallet, unlockWallet as backendUnlockWallet, useKeycloak } from '../auth/keycloakProvider';

// Custom event for wallet session errors
export const WALLET_SESSION_ERROR_EVENT = 'walletSessionError';

interface WalletContextType {
    isUnlocked: boolean;
    isConfigured: boolean;
    unlock: (pin: string) => Promise<boolean>;
    lock: () => void;
    refreshUnlockStatus: () => void;
    showUnlockModal: boolean;
    setShowUnlockModal: (show: boolean) => void;
    showSetupModal: boolean;
    setShowSetupModal: (show: boolean) => void;
    pendingAction: (() => void) | null;
    setPendingAction: (action: (() => void) | null) => void;
    requireWalletUnlock: (action: () => void) => void;
    setupWallet: (pin: string) => Promise<boolean>;
}

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
    const { setupWallet: backendSetupWallet } = useKeycloak();
    
    // Initialize with backend session state
    const [isUnlocked, setIsUnlocked] = useState(() => checkWalletUnlocked());
    const [isConfigured, setIsConfigured] = useState(() => {
        return localStorage.getItem('walletConfigured') === 'true';
    });
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [showSetupModal, setShowSetupModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

    // Refresh unlock status from backend session
    const refreshUnlockStatus = useCallback(() => {
        setIsUnlocked(checkWalletUnlocked());
    }, []);

    // Check wallet status periodically and on mount
    useEffect(() => {
        refreshUnlockStatus();
        
        // Check every 5 seconds to sync with backend session
        const interval = setInterval(() => {
            refreshUnlockStatus();
        }, 5000);

        return () => clearInterval(interval);
    }, [refreshUnlockStatus]);

    // Listen for wallet session errors from API calls
    useEffect(() => {
        const handleWalletSessionError = (event: Event) => {
                console.log('🔓 Wallet session error detected, showing unlock modal');
            
            // Clear the wallet session
            clearWalletSession();
            setIsUnlocked(false);
            
            // Show unlock modal
            setShowUnlockModal(true);
        };

        window.addEventListener(WALLET_SESSION_ERROR_EVENT, handleWalletSessionError);
        
        return () => {
            window.removeEventListener(WALLET_SESSION_ERROR_EVENT, handleWalletSessionError);
        };
    }, []);

    const setupWallet = useCallback(async (pin: string): Promise<boolean> => {
        try {
            console.log('🔧 Setting up wallet...');
            
            // Call the backend to setup wallet
            await backendSetupWallet(pin);
            console.log('✅ Wallet setup successful');
            
            // Store configuration
            localStorage.setItem('walletConfigured', 'true');
            setIsConfigured(true);
            
            // Unlock the wallet automatically after setup
            const sessionData = await backendUnlockWallet(pin);
            setWalletSession({
                sessionToken: sessionData.sessionToken,
                walletAddress: sessionData.walletAddress,
                expiresAt: new Date(sessionData.expiresAt)
            });
            
            setIsUnlocked(true);
            return true;
        } catch (err) {
            console.error('❌ Failed to setup wallet:', err);
            return false;
        }
    }, [backendSetupWallet]);

    const unlock = useCallback(async (pin: string): Promise<boolean> => {
        try {
            console.log('🔓 Attempting to unlock wallet...');
            
            // Call the backend to unlock wallet
            const sessionData = await backendUnlockWallet(pin);
            console.log('✅ Wallet unlocked successfully:', sessionData);
            
            // Set the wallet session in session storage
            setWalletSession({
                sessionToken: sessionData.sessionToken,
                walletAddress: sessionData.walletAddress,
                expiresAt: new Date(sessionData.expiresAt)
            });
            
            // Verify session was stored correctly
            const storedSession = getWalletSession();
            if (!storedSession) {
                console.error('❌ Session was not stored correctly in sessionStorage');
                throw new Error('Failed to store wallet session');
            }
            console.log('✅ Session verified in storage:', storedSession.sessionToken.substring(0, 10) + '...');
            
            // Update local state
            setIsUnlocked(true);
            localStorage.setItem('walletConfigured', 'true');
            setIsConfigured(true);
            
            return true;
        } catch (err) {
            console.error('❌ Failed to unlock wallet:', err);
            setIsUnlocked(false);
            return false;
        }
    }, []);

    const lock = useCallback(async () => {
        try {
            console.log('🔒 Locking wallet...');
            await backendLockWallet(); // This also calls clearWalletSession()
            console.log('✅ Wallet locked successfully');
        } catch (err) {
            console.error('❌ Failed to lock wallet on backend:', err);
            // Clear session anyway on error
            clearWalletSession();
        }
        setIsUnlocked(false);
    }, []);

    const requireWalletUnlock = useCallback((action: () => void) => {
        if (!isConfigured) {
            setPendingAction(() => action);
            setShowSetupModal(true);
        } else if (isUnlocked) {
            action();
        } else {
            setPendingAction(() => action);
            setShowUnlockModal(true);
        }
    }, [isUnlocked, isConfigured]);

    const value: WalletContextType = {
        isUnlocked,
        isConfigured,
        unlock,
        lock,
        refreshUnlockStatus,
        showUnlockModal,
        setShowUnlockModal,
        showSetupModal,
        setShowSetupModal,
        pendingAction,
        setPendingAction,
        requireWalletUnlock,
        setupWallet,
    };

    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
    const context = useContext(WalletContext);
    if (context === undefined) {
        throw new Error('useWallet must be used within a WalletProvider');
    }
    return context;
}
